/* ============================================================
 * <lion-product-form> — wraps the product page <form action="/cart/add">
 * Intercepts submit, POSTs via AJAX, publishes cart-update so the
 * <lion-cart-drawer> refreshes and opens.
 * ============================================================ */

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.querySelector('[name=id]')?.removeAttribute('disabled');
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.submitButton = this.querySelector('[type="submit"]');
    this.errorTarget = this.querySelector('[data-product-form-error]');
    this.cartType = document.querySelector('lion-cart-drawer') ? 'drawer' : (window.lionCartType || 'page');

    // Personalizzazione Duda Bloom: la casella del dipinto sta sotto le varianti,
    // quindi fuori da questo elemento. Si cerca risalendo al contenitore del
    // prodotto, non con una query su tutto il documento: in pagina possono
    // esserci più prodotti (prodotto in evidenza, quick-add) e prenderebbe
    // la casella sbagliata.
    this.productScope = this.closest('[data-product-id]');
    this.addon = this.productScope?.querySelector('[data-painted-addon]') || null;
    this.addonToggle = this.addon?.querySelector('[data-painted-addon-toggle]');
    this.addonPanel = this.addon?.querySelector('[data-painted-addon-panel]');
    this.addonBrief = this.addon?.querySelector('[data-painted-addon-brief]');
    this.addonToggle?.addEventListener('change', () => {
      const on = this.addonToggle.checked;
      if (this.addonPanel) this.addonPanel.hidden = !on;
      this.addon?.classList.toggle('painted-addon--selected', on);
      if (on) this.addonBrief?.focus();
      else this.handleErrorMessage();
    });
  }

  /**
   * La riga del servizio di pittura, se il cliente l'ha chiesta.
   * Torna null quando la casella è spenta, e undefined quando è accesa ma
   * manca il soggetto: senza quello non si saprebbe cosa dipingere.
   */
  paintedAddonLine() {
    if (!this.addonToggle?.checked) return null;
    const brief = (this.addonBrief?.value || '').trim();
    if (!brief) return undefined;
    const id = Number(this.addon.dataset.addonVariantId);
    if (!Number.isFinite(id) || id <= 0) return null;
    return {
      id,
      quantity: 1,
      properties: { [window.lionStrings?.paintedBriefProperty || 'Cosa dipingere']: brief },
    };
  }

  async onSubmit(event) {
    if (this.cartType === 'page') return; // let native submit go to /cart
    event.preventDefault();
    if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

    this.handleErrorMessage();

    const addonLine = this.paintedAddonLine();
    if (addonLine === undefined) {
      this.handleErrorMessage(
        window.lionStrings?.paintedBriefMissing || 'Scrivi cosa vuoi che dipingiamo sulla borsa.'
      );
      this.addonBrief?.focus();
      return;
    }

    this.submitButton?.setAttribute('aria-disabled', 'true');
    this.submitButton?.classList.add('loading');

    const formData = new FormData(this.form);
    formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);

    try {
      // Con il dipinto servono due righe nel carrello, e /cart/add accetta più
      // articoli solo in JSON. Senza dipinto si resta sul FormData di sempre,
      // che porta con sé quantità e proprietà già presenti nel form.
      let requestInit;
      if (addonLine) {
        const items = [
          {
            id: Number(formData.get('id')),
            quantity: Number(formData.get('quantity') || 1),
          },
          addonLine,
        ];
        requestInit = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: 'cart-drawer', sections_url: window.location.pathname }),
        };
      } else {
        requestInit = {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        };
      }

      const res = await fetch(`${window.shopUrls.cart_add}`, requestInit);
      const data = await res.json();

      if (data.status) {
        // error payload from Shopify: { status, description, message }
        this.handleErrorMessage(data.description || data.message || 'Error adding to cart');
        window.publish?.(window.PUB_SUB_EVENTS.cartError, { source: 'lion-product-form', message: data.description });
        return;
      }

      // Ask lion-cart-drawer to refresh itself from returned section HTML (if any)
      const drawer = document.querySelector('lion-cart-drawer');
      const sectionHtml = data.sections?.['cart-drawer'];
      if (drawer && sectionHtml) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        const fresh = doc.querySelector('lion-cart-drawer');
        if (fresh) drawer.innerHTML = fresh.innerHTML;
        drawer.querySelectorAll('[data-cart-drawer-close]').forEach((el) =>
          el.addEventListener('click', () => drawer.close())
        );
        drawer.updateHeaderBadge();
      }

      // Riporta la casella a spenta: il cliente che aggiunge una seconda borsa
      // deve riscegliere, altrimenti si ritrova un dipinto che non ha chiesto.
      if (addonLine && this.addonToggle) {
        this.addonToggle.checked = false;
        if (this.addonPanel) this.addonPanel.hidden = true;
        if (this.addonBrief) this.addonBrief.value = '';
        this.addon?.classList.remove('painted-addon--selected');
      }

      window.publish?.(window.PUB_SUB_EVENTS.cartUpdate, {
        source: this.dataset.source || 'lion-product-form',
        productVariantId: formData.get('id')
      });
    } catch (err) {
      console.error('[lion-product-form] add-to-cart failed:', err);
      this.handleErrorMessage('Network error. Please try again.');
    } finally {
      this.submitButton?.removeAttribute('aria-disabled');
      this.submitButton?.classList.remove('loading');
    }
  }

  handleErrorMessage(message = false) {
    if (!this.errorTarget) return;
    if (message === false) {
      this.errorTarget.hidden = true;
      this.errorTarget.textContent = '';
      return;
    }
    this.errorTarget.hidden = false;
    this.errorTarget.textContent = message;
  }
}
if (!customElements.get('lion-product-form')) {
  customElements.define('lion-product-form', ProductForm);
}
